import { ReactElement, useCallback, useState } from 'react';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { Text } from '@mantine/core';

import LoginForm from '../components/LoginForm';
import ForgotPasswordForm from '../components/ForgotPasswordForm';
import SamlRedirect from '../components/SamlRedirect';
import Unauthorized from '../components/Unauthorized';
import WelcomeMessage from '../components/WelcomeMessage';

import { useModal } from '@/hooks/useModal';
import { useSignIn } from '../hooks/useSignIn';
import { safeDecodeRedirect } from '@/utils/authRedirect';

import LogoMWhite from '@/assets/logo/logo-m-white.svg';

import classes from './Auth.module.css';

const Login = (): ReactElement => {
  const {
    loginPageState,
    signIn,
    nodeRef,
    isLoginReady,
    authInfo,
    isGettingType,
    loadingSignIn,
    onCancelOrBack,
    redirectParam,
    onUnauthorizedPrecheck,
  } = useSignIn();

  const { Modal, modalProps } = useModal({
    initialOpenState: loginPageState && !!loginPageState.modal_text,
    onClose: () => window.history.replaceState({}, document.title),
  });

  const { username, auth_type: authType } = authInfo || {};
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const renderMessage = useCallback(
    () => (
      <WelcomeMessage
        username={username}
        onClick={onCancelOrBack}
        isLoginReady={isLoginReady}
        showBackButton={authInfo?.auth_type !== 'SAML'}
      />
    ),
    [authInfo, isLoginReady, onCancelOrBack, username]
  );

  return (
    <div className={classes.page} data-mantine-color-scheme="light">
      <div className={classes.wrapper}>
        <div className={classes.hero}>
          <div className={classes.heroBrand}>
            <LogoMWhite className={classes.heroLogo} aria-hidden="true" />
            <p className={classes.heroTagline}>
              <span>Mercury</span>
              <span>Unified Risk Intelligence</span>
            </p>
          </div>

          <h1 className={classes.heroHeading}>Know your risk. Act before it moves.</h1>

          <div className={classes.heroCopy}>
            <p className={classes.heroCopyTitle}>
              Mercury gives lean European security teams a unified view of their exposure &mdash;
              threats, vulnerabilities, governance &mdash; so every finding arrives with context,
              likelihood, and a clear next step.
            </p>
            <p className={classes.heroCopyText}>
              Log in to see your current risk posture.
              <br />
              Not a feed. Not a dashboard of alerts.
              <br />A view of what matters to your organisation right now &mdash; and what to do
              about it.
            </p>
          </div>
        </div>

        <div className={classes.cardColumn}>
          <div className={classes.content}>
            <h2 className={classes.cardTitle}>
              {showForgotPassword ? 'Password Reset' : 'Log into your account'}
            </h2>

            {showForgotPassword ? (
              <ForgotPasswordForm onBack={() => setShowForgotPassword(false)} />
            ) : (
              (!isGettingType || loginPageState?.avoidPrecheck) && (
                <SwitchTransition>
                  <CSSTransition
                    key={username ? 'login' : 'unauthorized'}
                    nodeRef={nodeRef}
                    timeout={200}
                    classNames={{
                      enter: classes.enter,
                      enterActive: classes.enterActive,
                      exit: classes.exit,
                      exitActive: classes.exitActive,
                    }}
                    className={classes.loginFade}
                  >
                    <div ref={nodeRef}>
                      {isLoginReady ? (
                        <div>
                          {authType === 'SAML' ? (
                            <SamlRedirect
                              username={username as string}
                              renderMessage={renderMessage}
                              onCancel={onCancelOrBack}
                              redirectPath={safeDecodeRedirect(redirectParam)}
                            />
                          ) : (
                            <LoginForm
                              username={username}
                              signIn={signIn}
                              isSigningIn={loadingSignIn}
                              renderMessage={renderMessage}
                              tfa={authType === '2FA'}
                              onForgotPassword={() => setShowForgotPassword(true)}
                            />
                          )}
                        </div>
                      ) : (
                        <Unauthorized
                          onUnauthorizedPrecheck={onUnauthorizedPrecheck}
                          redirectParam={redirectParam}
                        />
                      )}
                    </div>
                  </CSSTransition>
                </SwitchTransition>
              )
            )}

            <p className={classes.supportText}>
              <strong>Troubleshooting access?</strong> Contact support{' '}
              <a href="mailto:support@quointelligence.eu" className={classes.supportLink}>
                here
              </a>
            </p>
          </div>
        </div>

        <Modal {...modalProps} title={loginPageState?.modal_title} size="500px">
          <Text size="xl" mt={15}>
            {loginPageState?.modal_text}
          </Text>
        </Modal>
      </div>
    </div>
  );
};

export default Login;
